import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ChevronDown, Sparkles } from "lucide-react";
import { IMAGES } from "../mock";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const y = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "120%"]);

  const scrollToServices = () => {
    document.querySelector("#services")?.scrollIntoView({ behavior: "smooth" });
  };

  const words = ["L'ART", "DE", "L'IMPORTATION", "AUTOMOBILE"];

  return (
    <section id="hero" ref={ref} className="relative h-screen min-h-[680px] overflow-hidden">
      {/* Background image with parallax */}
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img
          src={IMAGES.heroSupercars}
          alt="Véhicules sportifs premium"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#08080a] via-[#08080a]/55 to-[#08080a]/70" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#08080a]/85 via-transparent to-transparent" />
      </motion.div>

      {/* Content */}
      <motion.div
        style={{ opacity, y: textY }}
        className="relative z-10 h-full max-w-7xl mx-auto px-5 md:px-8 flex flex-col justify-center"
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          className="flex items-center gap-2 mb-6"
        >
          <span className="glass rounded-full px-4 py-2 text-xs font-display tracking-[0.2em] uppercase text-white/80 flex items-center gap-2">
            <Sparkles size={14} className="text-[#ff6a00]" />
            Courtage & Importation Automobile
          </span>
        </motion.div>

        <h1 className="section-title text-5xl sm:text-6xl md:text-8xl text-white max-w-4xl">
          {words.map((w, i) => (
            <span key={i} className="inline-block overflow-hidden mr-4 md:mr-6">
              <motion.span
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{
                  delay: 0.4 + i * 0.12,
                  duration: 0.9,
                  ease: [0.16, 1, 0.3, 1],
                }}
                className={`inline-block ${i === 3 ? "racing-text" : ""}`}
              >
                {w}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.7 }}
          className="mt-8 text-lg md:text-xl text-white/70 max-w-xl leading-relaxed"
        >
          Trouvez, importez et immatriculez la voiture de vos rêves, quel que
          soit votre budget. Un service clé en main depuis l'Allemagne, l'Italie
          et la Belgique.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.7 }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <button onClick={scrollToServices} className="btn-racing">
            Découvrir mes services
            <ArrowRight size={18} />
          </button>
          <button
            onClick={() => document.querySelector("#formations")?.scrollIntoView({ behavior: "smooth" })}
            className="btn-ghost"
          >
            Voir les formations
          </button>
        </motion.div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.button
        onClick={scrollToServices}
        style={{ opacity }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-white/50 hover:text-white transition-colors"
      >
        <span className="text-[10px] font-display tracking-[0.25em] uppercase">Scroll</span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
        >
          <ChevronDown size={22} />
        </motion.div>
      </motion.button>
    </section>
  );
};

export default Hero;
